// Proxy base — CRA dev server forwards /api/* to localhost:3001 (Express proxy)
// which injects the Voxbone apikey header before calling the Voxbone API.
// REACT_APP_API_BASE_URL points this at a separately-hosted backend when the
// frontend is built and served from a different origin.

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
const BASE = `${API_BASE}/api/voxbone`;

function authHeaders() {
  const t = localStorage.getItem('portal_token') || '';
  return t ? { Authorization: `Bearer ${t}` } : {};
}

async function get(path, params = {}) {
  const qs  = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}${path}${qs ? '?' + qs : ''}`, { headers: authHeaders() });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || body.message || `HTTP ${res.status}`);
  }
  return res.json();
}

async function post(path, body) {
  const res = await fetch(`${BASE}${path}`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body:    JSON.stringify(body),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || data.message || `HTTP ${res.status}`);
  }
  return res.json();
}

// ── Voice URIs (live from Voxbone) ───────────────────────────────────────────

export async function fetchVoiceUris() {
  const data = await get('/configuration/voiceuri', { pageNumber: 0, pageSize: 100 });
  const list = data.voiceUris || data.voice_uris || data.data || [];
  return list.map(u => ({
    voiceUriId:  u.voiceUriId  ?? u.voice_uri_id ?? u.id,
    description: u.description ?? u.name ?? '',
    uri:         u.uri         ?? u.voiceUri     ?? u.address ?? '',
    protocol:    u.voiceUriProtocol ?? u.protocol ?? 'SIP',
  }));
}

// ── DID inventory (from local DB cache, filtered by customer on server) ───────
// Returns [] on 401/404/503 so callers degrade gracefully.

export async function fetchDids() {
  try {
    const res = await fetch(`${API_BASE}/api/dids`, { headers: authHeaders() });
    if (!res.ok) {
      if (res.status === 401 || res.status === 404 || res.status === 503) return [];
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `HTTP ${res.status}`);
    }
    const data = await res.json();
    return (data.dids || []).map(d => ({
      didId:         d.didId,
      e164:          d.e164,
      description:   d.description,
      countryCodeA3: d.countryCodeA3,
      voiceUriId:    d.voiceUriId,
      customerId:    d.customerId    ?? null,
      customerName:  d.customerName  ?? null,
      siteId:        d.siteId        ?? null,
      siteName:      d.siteName      ?? null,
      teamsUserId:   d.teamsUserId   ?? null,
      teamsUserName: d.teamsUserName ?? null,
      zoomUserId:    d.zoomUserId    ?? null,
      zoomUserName:  d.zoomUserName  ?? null,
    }));
  } catch (e) {
    if (e.message.includes('404') || e.message.includes('503')) return [];
    throw e;
  }
}

// ── Apply a Voice URI to a list of DID IDs ───────────────────────────────────
// Voxbone endpoint: POST /v1/configuration/didConfiguration
// Body: { "didId": "<string>", "voiceUriId": "<string>" } — one call per DID.

export async function applyVoiceUri(didIds, voiceUriId) {
  for (const id of didIds) {
    await post('/configuration/didConfiguration', {
      didId:      String(id),
      voiceUriId: String(voiceUriId),
    });
  }
}

// ── Server health ─────────────────────────────────────────────────────────────

export async function checkHealth() {
  const res = await fetch(`${API_BASE}/api/health`);
  return res.json();
}
